import {
  useHistoryDictionaries,
  useHistoryOperations,
  useHistoryPageFilters,
  useOpenStatementModalFromQuery,
  useStuckState,
} from '../model';
import { buildOperationsGetQueryParams, filterOperations } from '../lib';
import {
  HistoryListFooter,
  HistoryOperationsList,
  HistoryOperationsListSkeleton,
  EmptyStateView,
} from './operations-list';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OperationDto, OperationFilter } from '@/shared/types';
import { ApiErrorText, ScrollTopButton } from '@/shared/components';
import {
  useAppSettingsStore,
  useElementHeight,
  useElementWidth,
  useMediaQuery,
  useScrollDetection,
} from '@/shared/lib';
import { cn } from '@/shared/utils';
import { useOperationsRefetch } from '@/entities/operation';
import {
  OperationDetailsDialog,
  OperationDetailsPanel,
} from './operation-details';
import { useGenerateStatementModal } from '@/features/generate-statement';
import {
  useInvoiceHistory,
  useInvoiceHistoryDialog,
} from '@/features/deposit-bank-transfer';
import { PendingInvoicesBar } from './pending-invoices';
import {
  HistoryExportButton,
  HistoryFilterSkeleton,
  HistoryOperationTypeTabs,
  HistoryDateFilterSelect,
} from './list-controls';
import { useVerificationInfoModal } from '@/entities/verification';
import { useSessionStore } from '@/entities/session';
import { useAccountsRefetch } from '@/entities/account';

const DETAILS_PANEL_MIN_CONTENT_WIDTH = 1040;
const PAGE_HORIZONTAL_PADDINGS = 72;
const DETAILS_PANEL_TOP_GAP = 24;

const HistoryPage = () => {
  const { t } = useTranslation();
  const { getLanguage } = useAppSettingsStore();
  const { session } = useSessionStore();

  const isVerified = session?.additional?.isVerified || false;
  const language = getLanguage();

  const { openGenerateStatementModal } = useGenerateStatementModal();
  const { openVerificationInfoModal } = useVerificationInfoModal();

  useOpenStatementModalFromQuery();

  const { refetchAll: refetchAccounts } = useAccountsRefetch({
    userId: session?.userId,
  });
  const { refetchAll: refetchOperations } = useOperationsRefetch({
    userId: session?.userId,
  });

  const {
    isLoading: isOperationsDictionariesLoading,
    error: operationsDictionariesError,
    operationTypes,
    dateFilters,
    operationTypeIcons,
    operationStatusIcons,
  } = useHistoryDictionaries();

  const { filters, setPageFilters, resetPeriod, isPeriodApplied } =
    useHistoryPageFilters({ operationTypes, dateFilters });

  const { ref: containerRef, hasScrolled } = useScrollDetection({
    threshold: 300,
    debounceMs: 100,
  });

  const { sentinelRef, isStuck } = useStuckState(containerRef);
  const { ref: filtersBarRef, height: filtersBarHeight } =
    useElementHeight<HTMLDivElement>();
  const { ref: listAreaRef, width: listAreaWidth } =
    useElementWidth<HTMLDivElement>();
  const isTabletUp = useMediaQuery('(min-width: 1068px)');
  const isPanelMode =
    isTabletUp &&
    listAreaWidth + PAGE_HORIZONTAL_PADDINGS >= DETAILS_PANEL_MIN_CONTENT_WIDTH;

  const [selectedOperationId, setSelectedOperationId] = useState<string>();

  const { data: pendingInvoices } = useInvoiceHistory();
  const { openInvoiceHistoryDialog } = useInvoiceHistoryDialog();
  const pendingInvoicesCount = pendingInvoices?.length ?? 0;

  const queryParams = useMemo(
    () => buildOperationsGetQueryParams(filters),
    [filters]
  );

  const {
    operations,
    isFirstFrameLoading,
    firstFrameError,
    isFirstFrameEmpty,
    hasMore,
    isFetchingMore,
    isLoadMoreError,
    isLoadMoreRequired,
    loadNextFrame,
    loadMore,
    retry,
  } = useHistoryOperations(queryParams);

  const filteredOperations = useMemo(
    () => filterOperations(operations, filters),
    [operations, filters]
  );

  const selectedOperation = useMemo(
    () =>
      selectedOperationId
        ? operations.find(
            operation => operation.operaionid === selectedOperationId
          )
        : undefined,
    [operations, selectedOperationId]
  );

  const closeOperationDetails = useCallback(
    () => setSelectedOperationId(undefined),
    []
  );

  const handleStatusChange = useCallback(() => {
    refetchAccounts();
    refetchOperations();
  }, [refetchAccounts, refetchOperations]);

  const handleOperationItemClick = useCallback((operation: OperationDto) => {
    setSelectedOperationId(operation.operaionid);
  }, []);

  const isOperationsListEmpty =
    isFirstFrameEmpty || (!filteredOperations.length && !hasMore);

  const scrollToTop = () => {
    containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSelectOperationType = useCallback(
    (value: OperationFilter) => {
      setPageFilters({ ...filters, filter: value });
    },
    [filters, setPageFilters]
  );

  const handleHistoryExportClick = useCallback(() => {
    if (!isVerified) {
      openVerificationInfoModal();
      return;
    }

    openGenerateStatementModal({
      dateFilter: filters?.datefilter,
      dateFrom: filters?.datefrom,
      dateTo: filters?.dateto,
    });
  }, [
    openGenerateStatementModal,
    filters,
    isVerified,
    openVerificationInfoModal,
  ]);

  const renderOperations = () => {
    if (firstFrameError) {
      return <ApiErrorText error={firstFrameError} className="px-[1.6rem]" />;
    }

    if (isFirstFrameLoading) {
      return <HistoryOperationsListSkeleton />;
    }

    if (isOperationsListEmpty) {
      return (
        <EmptyStateView
          isPeriodApplied={isPeriodApplied}
          onPeriodReset={resetPeriod}
        />
      );
    }

    return (
      <>
        <HistoryOperationsList
          operations={filteredOperations}
          language={language}
          operationTypeIcons={operationTypeIcons}
          operationStatusIcons={operationStatusIcons}
          onOperationItemClick={handleOperationItemClick}
          selectedOperationId={selectedOperation?.operaionid}
        />

        <HistoryListFooter
          hasMore={hasMore}
          isFetchingMore={isFetchingMore}
          isLoadMoreError={isLoadMoreError}
          isLoadMoreRequired={isLoadMoreRequired}
          onReachEnd={loadNextFrame}
          onLoadMore={loadMore}
          onRetry={retry}
        />
      </>
    );
  };

  return (
    <div
      className="flex flex-col h-full px-[0.8rem] tablet:px-[3.6rem] pb-[5.8rem] overflow-y-auto overflow-x-hidden"
      ref={containerRef as React.RefObject<HTMLDivElement>}>
      <h1 className="hero6 text-primary px-[1.6rem] pt-[1.6rem] pb-[1rem] tablet:pb-[0.4rem]">
        {t('historyPage.title')}
      </h1>

      {operationsDictionariesError ? (
        <ApiErrorText
          error={operationsDictionariesError}
          className="px-[1.6rem]"
        />
      ) : (
        <>
          <div ref={sentinelRef} />

          <div
            ref={filtersBarRef}
            className={cn(
              'sticky top-0 z-10 -mx-[0.8rem] tablet:-mx-[3.6rem] flex flex-col gap-[1.6rem] tablet:flex-row tablet:items-center bg-background/90 backdrop-blur-[0.4rem] px-(--bar-px) pt-[0.6rem] pb-[1.2rem] tablet:py-[1.2rem] transition-[padding,box-shadow] duration-200',
              '[--bar-px:2.4rem] tablet:[--bar-px:5.2rem]',
              isStuck &&
                '[--bar-px:1.6rem] tablet:[--bar-px:2.4rem] tablet:gap-[0.8rem] shadow-[inset_0_-1px_0_var(--stroke-bg)]'
            )}>
            {isOperationsDictionariesLoading ? (
              <HistoryFilterSkeleton />
            ) : (
              <>
                <HistoryOperationTypeTabs
                  onValueChange={handleSelectOperationType}
                  value={filters.filter}
                  options={operationTypes}
                  className="-mx-(--bar-px) px-(--bar-px) scroll-px-(--bar-px) tablet:mx-0 tablet:px-0 tablet:scroll-px-0"
                />

                <div className="flex shrink-0 flex-wrap items-center gap-x-[1.6rem] gap-y-[0.8rem] tablet:flex-nowrap">
                  <HistoryDateFilterSelect
                    filters={filters}
                    onFiltersChange={setPageFilters}
                    options={dateFilters}
                    onResetPeriod={resetPeriod}
                  />

                  <HistoryExportButton onClick={handleHistoryExportClick} />
                </div>
              </>
            )}
          </div>

          {pendingInvoicesCount > 0 && (
            <div className="mt-[1.2rem] px-[1.6rem] tablet:px-[1.2rem]">
              <PendingInvoicesBar
                count={pendingInvoicesCount}
                onClick={() => openInvoiceHistoryDialog()}
              />
            </div>
          )}

          <div
            ref={listAreaRef}
            className="mt-[2.4rem] flex flex-1 items-start gap-[3.6rem]">
            <div className="flex min-w-0 flex-1 flex-col self-stretch">
              {renderOperations()}
            </div>

            {selectedOperation && isPanelMode && (
              <OperationDetailsPanel
                operation={selectedOperation}
                topOffset={filtersBarHeight + DETAILS_PANEL_TOP_GAP}
                onClose={closeOperationDetails}
                onStatusChange={handleStatusChange}
              />
            )}
          </div>

          {selectedOperation && !isPanelMode && (
            <OperationDetailsDialog
              operation={selectedOperation}
              onClose={closeOperationDetails}
              onStatusChange={handleStatusChange}
            />
          )}
        </>
      )}

      {hasScrolled && (
        <ScrollTopButton
          onClick={scrollToTop}
          className="absolute bottom-[3.4rem] inset-x-0 mx-auto"
        />
      )}
    </div>
  );
};

export { HistoryPage };
